import React, { useState, useEffect } from 'react';
import { Variables, variables } from '../../Variables.js';
import { Bar } from 'react-chartjs-2';
import Chart from 'chart.js/auto';

function formatGameId(gameId) {

    const parts = gameId.split('_');
    const date = parts[0];
    const game = parts[1];

    const year = date.substring(0, 4);
    const month = date.substring(4, 6);
    const day = date.substring(6, 8);

    const formattedDate = `${month}/${day}/${year}`;

    return `${game}`;
}

function formatDate(gameId, includeYear = true) {
    const parts = gameId.split('_');
    const date = parts[0];
    // const game = parts[1]; // Assuming you're not using this part for the date format

    const year = date.substring(2, 4);
    const month = date.substring(4, 6);
    const day = date.substring(6, 8);

    const formattedDate = includeYear ? `${month}/${day}/${year}` : `${month}/${day}`;

    return formattedDate;
}

const Widget = ({ label, onClick, isSelected }) => {
    const widgetClass = `widget ${isSelected ? 'widget-selected' : ''}`;
    return (
        <button className={widgetClass} onClick={onClick}>
            {label}
        </button>
    );
};


const WidgetList = ({ onWidgetSelect }) => {
    const [selectedWidget, setSelectedWidget] = useState('fantasy'); // Default selection

    useEffect(() => {
        // Trigger initial widget selection on component mount
        onWidgetSelect('fantasy');
    }, []);

    const handleWidgetClick = (label) => {
        setSelectedWidget(label);
        onWidgetSelect(label); // Trigger the callback function with the selected widget
    };

    const validWidgetNames = ['pts', 'rebs', 'asts', 'fantasy'];

    return (
        <div>
            {validWidgetNames.map((label) => (
                <Widget
                    key={label}
                    label={label}
                    isSelected={selectedWidget === label}
                    onClick={() => handleWidgetClick(label)}
                />
            ))}
        </div>
    );
};

function LastFive(props) {
    const [shouldRenderChart, setShouldRenderChart] = useState(false);
    const [tableData, setTableData] = useState([]);
    const [selectedWidget, setSelectedWidget] = useState('fantasy'); // Store the selected widget
    const [chartOptions, setChartOptions] = useState({});
    const [datasetLabel, setDatasetLabel] = useState('Fantasy Points');


    const [barGraphData, setBarGraphData] = useState({
        labels: [],
        datasets: [{
            label: '', // This will be set based on the selected widget
            data: [],
            backgroundColor: 'rgba(53, 162, 235, 0.5)'
        }]
    });

    // Adjust the chartOptions if needed

    useEffect(() => {
        const fetchData = async () => {
            try {
                const response = await fetch(variables.API_URL + 'Stats/getLastFive/' + props.data + "/" + variables.CURR_NBA_SEASON);
                const data = await response.json();
                const labels = data.map(item => formatGameId(item.gameID));
                let includeYear = true; // Default to including the year

                if (window.innerWidth <= 480) {
                    includeYear = false; // Do not include the year for screens 480px or narrower
                }

                const dates = data.map(item => formatDate(item.gameID,includeYear));

                // Reset the data arrays
                let datasetData = [];
                let datasetLabel = '';

                switch (selectedWidget) {
                    case 'pts':
                        datasetData = data.map(item => parseFloat(item.pts));
                        datasetLabel = 'Points';
                        break;
                    case 'rebs':
                        datasetData = data.map(item => parseFloat(item.offReb) + parseFloat(item.defReb));
                        datasetLabel = 'Rebounds';
                        break;
                    case 'asts':
                        datasetData = data.map(item => parseFloat(item.ast));
                        datasetLabel = 'Assists';
                        break;
                    case 'fantasy':
                    default:
                        datasetData = data.map(item => parseFloat(item.fantasyPoints));
                        datasetLabel = 'Fantasy Points';
                        break;
                }
                setDatasetLabel(datasetLabel); // Update the datasetLabel state

                // Reverse data for chronological order
                labels.reverse();
                dates.reverse();
                datasetData.reverse();

                setChartOptions({
                    maintainAspectRatio: false,
                    responsive: true,
                    plugins: {
                        tooltip: {
                            callbacks: {
                                title: function (tooltipItems) {
                                    // Assuming that all items in a tooltip share the same title
                                    let tooltipItem = tooltipItems[0];

                                    // Access the original label
                                    let originalLabel = tooltipItem.chart.data.labels[tooltipItem.dataIndex];

                                    // Accessing the additional tooltip text (date in this case)
                                    let tooltipText = tooltipItem.chart.data.datasets[tooltipItem.datasetIndex].tooltipText[tooltipItem.dataIndex];

                                    // Return the text to be displayed as the tooltip title
                                    return `${originalLabel} (${tooltipText})`;
                                },
                                label: function (context) {

                                    // Accessing the label and the value
                                    let label = context.dataset.label;
                                    let value = context.parsed.y;

                                    // Accessing the additional tooltip text (date in this case)
                                    let tooltipText = context.chart.data.datasets[context.datasetIndex].tooltipText[context.dataIndex];

                                    // Return the text to be displayed in the tooltip
                                    return `${label}: ${value}`;
                                }
                            }
                        }
                    }
                });

                setBarGraphData({
                    labels,
                    datasets: [{
                        label: datasetLabel,
                        data: datasetData,
                        tooltipText: dates,
                        backgroundColor: 'rgba(53, 162, 235, 0.5)'
                    }]
                });

                // Update the table data
                const gameData = labels.map((label, index) => {
                    const dataKey = selectedWidget; // Directly use the selectedWidget as the key
                    return {
                        label,
                        dates: dates[index],
                        [dataKey]: datasetData[index], // Use the selectedWidget as the dynamic key
                    };
                });

                setTableData(gameData);
                setShouldRenderChart(true);
            } catch (error) {
                console.error('Error fetching data:', error);
            }
        };

        fetchData();
    }, [props.data, selectedWidget, shouldRenderChart]);

    const handleWidgetSelection = (widget) => {
        setSelectedWidget(widget);
        setShouldRenderChart(false); // Trigger re-render of chart and table with new data
    };

    const renderTable = () => (
        <table className="modalTable">
            <thead>
                <tr>
                    <th className="modalTh">Day</th>
                    <th className="modalTh">Game</th>
                    <th className="modalTh">{datasetLabel}</th>
                </tr>
            </thead>
            <tbody>
                {tableData.map((game, index) => (
                    <tr key={index}>
                        <td className="modalTd">{game.dates}</td>
                        <td className="modalTd">{game.label}</td>
                        <td className="modalTd">{game[selectedWidget]}</td>
                    </tr>
                ))}
            </tbody>
        </table>
    );

    return (
        <div className="canvas">
            <center>
                <WidgetList onWidgetSelect={handleWidgetSelection} />
                <h3>Last Five Games </h3>
            </center>
            {shouldRenderChart && <Bar options={chartOptions} data={barGraphData} />}
            {shouldRenderChart && renderTable()}
        </div>
    );
}

export default LastFive;



